<template>

  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              Settings
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <!-- Table Container Card -->
    <b-form @submit.prevent="submitForm">
      <validation-observer
        ref="reservationSettingForm"
        #default="{invalid}"
      >
        <b-card
          header-tag="header"
        >
          <template #header>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              class="primary-button-with-background mr-3"
            >
              <feather-icon
                icon="SettingsIcon"
                class=""
                size="20"
              />
            </b-button>
            <h3 class="align-middle mr-auto">
              Reservations by Agent
            </h3>
          </template>

          <b-row>
            <b-col md="12">
              <b-form-group
                label="Daily Reservations Quota"
                label-cols-md="2"
                label-for="dailyReservationQuota"
                description="The maximum “Reserved” reservations an agent can create per calendar day (00.00am to 11.59pm). Reservations released on the same day will restore the quota for that day and are thus not counted in the quota."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Daily Reservations Quota"
                  vid="dailyReservationQuota"
                  rules="required"
                >
                  <b-form-input
                    id="dailyReservationQuota"
                    v-model="dailyReservationQuota"
                    :state="(errors.length > 0) ? false : null"
                    name="dailyReservationQuota"
                    placeholder="5"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="dailyReservationQuotaValidation"
                    class="text-danger"
                  >
                    {{ dailyReservationQuotaError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Reservation Duration"
                label-cols-md="2"
                label-for="reservationDuration"
                description="The default duration, in hours, of an agent-created reservation."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Reservation Duration"
                  vid="reservationDuration"
                  rules="required"
                >
                  <b-form-input
                    id="reservationDuration"
                    v-model="reservationDuration"
                    :state="(errors.length > 0) ? false : null"
                    name="reservationDuration"
                    placeholder="48"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="reservationDurationValidation"
                    class="text-danger"
                  >
                    {{ reservationDurationError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Minimum Consecutive Interval"
                label-cols-md="2"
                label-for="intervalDuration"
                description="The minimum duration, in hours, an agent must wait before a repeat reservation for the same lot can be created, from release date/time."
              >
                <validation-provider
                  #default="{ errors }"
                  name="Minimum Consecutive Interval"
                  vid="intervalDuration"
                  rules="required"
                >
                  <b-form-input
                    id="intervalDuration"
                    v-model="intervalDuration"
                    :state="(errors.length > 0) ? false : null"
                    name="intervalDuration"
                    placeholder="2"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="intervalDurationValidation"
                    class="text-danger"
                  >
                    {{ intervalDurationError }}
                  </small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <b-nav class="wrap-border save-nav">
          <li
            class="nav-item ml-auto"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              @click="cancelEdit"
            >
              Cancel
            </b-button>
          </li>
          <li
            class="nav-item mr-1"
          >
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              <span class="align-middle">Save Changes</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BForm, BButton, BNav,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BNav,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      reservationSetting: {},
      dailyReservationQuota: '',
      dailyReservationQuotaValidation: false,
      dailyReservationQuotaError: '',
      reservationDuration: '',
      reservationDurationValidation: false,
      reservationDurationError: '',
      intervalDuration: '',
      intervalDurationValidation: false,
      intervalDurationError: '',
      // validation rules
      required,
    }
  },
  created() {
    this.getReservationSettings()
  },
  methods: {
    getReservationSettings() {
      this.$http.get('reservation/reservation-settings')
        .then(response => {
          this.reservationSetting = response.data
          if (response.data) {
            this.dailyReservationQuota = response.data.dailyReservationQuota
            this.reservationDuration = response.data.reservationDuration
            this.intervalDuration = response.data.intervalDuration
          }
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    cancelEdit() {
      this.$swal({
        title: 'Are you sure?',
        html: 'Selecting Cancel will <strong>undo any changes</strong> you have made. This action cannot be undone.',
        // eslint-disable-next-line global-require
        imageUrl: require('@/assets/images/icons/warning.png'),
        imageWidth: 80,
        imageHeight: 80,
        imageAlt: 'Custom Icon',
        reverseButtons: true,
        showCancelButton: true,
        confirmButtonText: 'Yes, Cancel!',
        cancelButtonText: 'No, Go Back',
        customClass: {
          confirmButton: 'btn btn-danger ml-1',
          cancelButton: 'btn btn-outline-primary',
        },
        buttonsStyling: false,
      })
        .then(result => {
          if (result.value) {
            this.getReservationSettings()
          }
        })
    },
    submitForm() {
      this.$refs.reservationSettingForm.validate().then(success => {
        if (success) {
          const formData = new FormData()

          formData.append('dailyReservationQuota', this.dailyReservationQuota)
          formData.append('reservationDuration', this.reservationDuration)
          formData.append('intervalDuration', this.intervalDuration)

          this.$http.patch('reservation/reservation-settings/store', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(response => {
              if (response.data.data) {
                this.$swal({
                  title: 'Changes have Saved',
                  html: response.data.message || '',
                  // eslint-disable-next-line global-require
                  imageUrl: require('@/assets/images/icons/save.png'),
                  imageWidth: 80,
                  imageHeight: 80,
                  imageAlt: 'Custom Icon',
                  showCancelButton: false,
                  allowOutsideClick: false,
                  confirmButtonText: 'Okay',
                  customClass: {
                    confirmButton: 'btn btn-primary',
                  },
                  buttonsStyling: false,
                })
                  .then(result => {
                    if (result.value) {
                      this.reservationSetting = response.data.data
                    }
                  })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: response.data.message || '',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
            .catch(error => {
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'dailyReservationQuota') {
                    this.dailyReservationQuotaError = validationError.msg
                    this.dailyReservationQuotaValidation = true
                  } else if (validationError.param === 'reservationDuration') {
                    this.reservationDurationError = validationError.msg
                    this.reservationDurationValidation = true
                  } else if (validationError.param === 'intervalDuration') {
                    this.intervalDurationError = validationError.msg
                    this.intervalDurationValidation = true
                  }
                })

                const items = document.getElementsByClassName('text-danger')
                window.scrollTo({
                  top: items[0].offsetTop,
                  behavior: 'smooth',
                })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
  },
}
</script>

<style scoped>
    .removeText {
      color:#D91B35;
      cursor: pointer;
    }
    .addOptionText {
      color: #104D9D;
      cursor: pointer;
    }
    .btn-group-toggle [class*=btn-outline-]:not(:last-child) {
      border-right-width: 1px !important;
    }
    .inlineElement {
      display: flex;
    }
</style>
